import { calculateDistance, rad2miles } from "./geometric";
import { regionalizeStates } from './cohorts';
import { isObject, trimString } from "./common";
import moment from 'moment';

export const topEvents = {
  drugsClasses: {
    title: 'Drug Class',
    shortTitle: 'Class',
    prop: 'drugclass',
  },
  drugs: {
    title: 'Drug Detail',
    shortTitle: 'Detail',
    prop: 'drugdetail',
  },
  drugsGenerics: {
    title: 'Drug Generic',
    shortTitle: 'Generic',
    prop: 'druggeneric',
  },
  drugsBrands: {
    title: 'Drug Brand',
    shortTitle: 'Brand',
    prop: 'drugbrand',
  },

  drugsForm: {
    title: 'Drug Form',
    shortTitle: 'Form',
    prop: 'drugform',
  },
  drugsStrength: {
    title: 'Drug Strength',
    shortTitle: 'Strength',
    prop: 'drugstrength',
  },
  drugsNdc: {
    title: 'Drug NDC',
    shortTitle: 'NDC',
    prop: 'drugndc',
  },
  // Added for USC Drugs
  uscClasses: {
    title: 'USC Drug Class',
    shortTitle: 'Class',
    prop: 'uscclass',
  },

  uscGenerics: {
    title: 'USC Drug Generic',
    shortTitle: 'Generic',
    prop: 'uscgeneric',
  },
  uscBrands: {
    title: 'USC Drug Brand',
    shortTitle: 'Brand',
    prop: 'uscbrand',
  },

  uscForm: {
    title: 'USc Drug Form',
    shortTitle: 'Form',
    prop: 'uscform',
  },
  uscStrength: {
    title: 'USC Drug Strength',
    shortTitle: 'Strength',
    prop: 'uscstrength',
  },
  uscNdc: {
    title: 'USC Drug NDC',
    shortTitle: 'NDC',
    prop: 'uscndc',
  },
  //
  diagnosesCategories: {
    title: 'Diagnosis Category',
    shortTitle: 'Category',
    showCode: true,
    prop: 'dxgroup',
  },
  diagnoses: {
    title: 'Diagnosis',
    showCode: true,
    prop: 'dxname',
  },

  diagnosesCombo: {
    title: 'Diagnosis',
    showCode: true,
    prop: 'dxcombo',
  },

  proceduresCategories: {
    title: 'Procedure Category',
    shortTitle: 'Category',
    showCode: true,
    prop: 'procclass',
  },
  procedures: {
    title: 'Procedure',
    showCode: true,
    prop: 'procdescrip',
  },

  proceduresCombo: {
    title: 'Procedure',
    showCode: true,
    prop: 'proccombo',
  },

  specialities: {
    title: 'Speciality',
    prop: 'speciality',
  },
  institutionsIDN: {
    title: 'Institutions IDN',
    shortTitle: 'IDN',
    prop: 'institutionsidn',
  },
  institutionsHCO: {
    title: 'Institutions HCO',
    shortTitle: 'HCO',
    prop: 'institutionshco',
  },
  institutionsPG: {
    title: 'Institutions PG',
    shortTitle: 'PG',
    prop: 'institutionspg',
  },
  territories: {
    title: 'Territory',
  },

  cohort: {
    title: 'Cohort',
    prop: 'cohort',
  }
};

export const topEventTypes = () => ({
  usc: ['uscclass','uscgeneric','uscbrand', 'uscform', 'uscstrength','uscndc'],
  drugs: ['drugclass', 'drugdetail', 'druggeneric', 'drugbrand', 'drugform', 'drugstrength', 'drugndc'],
  diagnoses: ['dxgroup', 'dxname', 'dxcombo'],
  procedures: ['procclass', 'procdescrip', 'proccombo'],
  speciality: ['speciality'],
});


export const costOfCareEvents = {
  er: {
    title: 'ER',
    prop: 'er',
  },
  costIn: {
    title: 'Inpatient',
    prop: 'costin',
  },
  costOut: {
    title: 'Outpatient',
    prop: 'costout',
  },
  prescription: {
    title: 'Prescriptions',
    prop: 'prescription',
  },
};

var filterSegments = (value, segment) => {
  var segmentChildrenNames = segment.children.reduce((acc, f) => { acc[f.cohortId] = f.name; return acc; }, {})
  return value.cohortId.map(id => segmentChildrenNames[id]).filter(f => f);
}

var segmentItem = f => ({
  title: 'Population',
  item: f,
  remove: function () {
    this.$emit('input', { ...this.value, cohortId: [this.segment.cohortId] });
  }
});

var timeItem = value => ({
  title: 'Time filter',
  item: value.timeFilter,
})

var genderItem = value => ({
  title: 'Gender',
  item: value.demo.sex === 1 ? 'Male' : 'Female',
  remove: function () {
    this.$emit('input', { ...this.value, demo: { ...this.value.demo, sex: -1 } });
  }
});

var ageItem = value => ({
  title: 'Age range',
  item: `${value.demo.ageFrom} - ${value.demo.ageTo}`,
  remove: function () {
    this.$emit('input', { ...this.value, demo: { ...this.value.demo, ageFrom: 0, ageTo: 90 } });
  }
});

var insuranceItem = value => ({
  title: 'Insurance',
  item: value.demo.medicalInsurance.join(', '),
  remove: function () {
    this.$emit('input', { ...this.value, demo: { ...this.value.demo, medicalInsurance: null } });
  }
});

var yearsItem = value => {
  var yearsRange = [value.years.min(), value.years.max()].unique();

  return {
    title: 'Year' + (yearsRange.length > 1 ? ' range' : ''),
    item: yearsRange.join(' - '),
    remove: function () {
      this.$emit('input', { ...this.value, years: null });
      this.$emit('years-removed');
    }
  };
};

var rectangleDimensions = rectangle => {
  var halfLat = (rectangle.swLngLat[1] + rectangle.neLngLat[1]) / 2;
  var width = calculateDistance([rectangle.swLngLat[0], halfLat], [rectangle.neLngLat[0], halfLat]);
  var height = calculateDistance([rectangle.swLngLat[0], rectangle.swLngLat[1]], [rectangle.swLngLat[0], rectangle.neLngLat[1]]);
  return `${Math.round(rad2miles(width))}x${Math.round(rad2miles(height))} mi`;
};
var rectangleAreaItem = value => ({
  title: 'Rectangle area',
  item: rectangleDimensions(value.area.rectangle),
  remove: function () {
    this.$emit('input', { ...this.value, area: { ...this.value.area, rectangle: null } });
    this.$emit('rectangle-removed');
  }
});

var circleDimensions = circle => `radius: ${Math.round(rad2miles(circle.r))} mi`;
var circleAreaItem = value => ({
  title: 'Circle area',
  item: circleDimensions(value.area.circle),
  remove: function () {
    this.$emit('input', { ...this.value, area: { ...this.value.area, circle: null } });
    this.$emit('circle-removed');
  }
});

var regionalizedStates = value => {
  var items = regionalizeStates((value.area || {}).states || []);
  var regions = items.filter(f => f.states.length > 1);
  var states = items.filter(f => f.states.length === 1);
  return regions.sortBy(f => f.label).concat(states.sortBy(f => f.label));
};
var statesItem = item => ({
  title: item.states.length > 1 ? 'Region' : 'State',
  item: item.label,
  remove: function () {
    var filtered = this.value.area.states.filter(state => !item.states.includes(state));
    this.$emit('input', {
      ...this.value, area: {
        ...this.value.area,
        states: filtered,
      }
    });
  }
});

var decilesItem = value => {
  var decilesRange = value.deciles.map(f => f).sort((a, b) => a - b)
    .reduce((acc, f) => {
      var group = acc.find(list => list.find(num => num === f - 1)) || [];
      group.push(f);
      if (!acc.includes(group))
        acc.push(group);
      return acc;
    }, [])
    .flatMap(f => f.length > 1 ? [`${f[0]} - ${f.last()}`] : f);

  return {
    title: 'Decile' + (decilesRange.length > 1 ? 's' : ''),
    item: decilesRange.join(', '),
    remove: function () {
      this.$emit('input', { ...this.value, deciles: null });
      this.$emit('deciles-removed');
    }
  };
};

var topEventItem = (item, prop, translation) => {
  var fullItem = ((translation || {})[prop] || {})[item] || item.label || item;
  return {
    ...topEvents[prop],
    item: trimString(40, fullItem),
    fullItem,
    remove: function () {
      if (this.value.translation && !isObject(item)) delete (this.value.translation[prop] || {})[item];
      this.$emit('input', { ...this.value, [prop]: this.value[prop].filter(f => f !== item) });
    }
  };
};

var costOfCareEventItem = (item, prop) => {
  var fullItem = item.label || item;
  return {
    ...costOfCareEvents[prop],
    item: trimString(40, fullItem),
    fullItem,
    remove: function () {
      this.$emit('input', { ...this.value, [prop]: this.value[prop].filter(f => f !== item) });
    }
  };
};

var payerItem = (item) => {
  return {
    title: 'Payer',
    item: item === 'Unknown' ? 'Other' : item,
    remove: function () {
      this.$emit('input', { ...this.value, payers: this.value.payers.filter(f => f !== item) });
    }
  };
};

var drugLinkItem = (link) => {
  return {
    title: 'Drug Link',
    item: `${link.source.name} - ${link.target.name}`,
    remove: function () {
      this.$emit('input', { ...this.value, drugLinks: this.value.drugLinks.filter(f => f !== link) });
    }
  };
};

var dateRangeTitle = value => {
  if (!value.dateRange) return null;

  var { from, to } = value.dateRange;
  if (from && to)
    return 'Date range';
  else if (from)
    return 'Date from';
  else if (to)
    return 'Date to';
};

var formatDate = date => moment.utc(date).format('M/YYYY');
var dateRangeItem = value => ({
  title: dateRangeTitle(value),
  item: [value.dateRange.from, value.dateRange.to].filter(f => f).map(formatDate).join(' - '),
  remove: function () {
    this.$emit('input', { ...this.value, dateRange: null });
  }
});

export const getRibonItems = (value, segment) => [
  ...(segment ? filterSegments(value, segment).map(segmentItem) : []),
  value.timeFilter ? timeItem(value) : null,
  value.demo && value.demo.sex > 0 ? genderItem(value) : null,
  value.demo && value.demo.ageFrom !== 0 ? ageItem(value) : null,
  value.demo && value.demo.medicalInsurance?.any() ? insuranceItem(value) : null,
  value.years ? yearsItem(value) : null,
  value.area && value.area.rectangle ? rectangleAreaItem(value) : null,
  value.area && value.area.circle ? circleAreaItem(value) : null,
  ...regionalizedStates(value).map(statesItem),
  value.deciles && value.deciles.length ? decilesItem(value) : null,
  ...(value.payers || []).sortBy(f => f).map(payerItem),
  ...Object.keys(topEvents)
    .filter(prop => value[prop] && value[prop].length)
    .flatMap(prop => (value[prop] || []).map(item => topEventItem(item, prop, value.translations))),
  ...Object.keys(costOfCareEvents)
    .filter(prop => value[prop] && value[prop].length)
    .flatMap(prop => (value[prop] || []).map(item => costOfCareEventItem(item, prop, value.translations))),
  ...(value.drugLinks || []).map(drugLinkItem),
  dateRangeTitle(value) ? dateRangeItem(value) : null,
].filter(f => f);

export const getFilterItems = liveFilters => {
  var items = Object.keys(topEvents)
    .flatMap(key => liveFilters[key] || []);
  if (liveFilters.area) {
    if (liveFilters.area.circle)
      items.push(liveFilters.area.circle);
    items = items.concat(liveFilters.area.states || []);
  }
  return items;
}

export const stringifyLiveFilters = liveFilters => {
  var keys = Object.keys(topEvents);
  return Object.keys(liveFilters)
    .reduce((acc, key) => {
      if (keys.includes(key))
        acc[key] = (liveFilters[key] || []).map(f => isObject(f) ? JSON.stringify(f) : f);
      else acc[key] = liveFilters[key];
      return acc;
    }, {});
}